// app/pages/CommonCodes.jsx
import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { commonCodeService } from '../services/commonCode.service';
import CommonCodeTable from '../components/commonCodes/CommonCodeTable';
import CommonCodeModal from '../components/commonCodes/CommonCodeModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const CommonCodes = () => {
    const [codeTypes, setCodeTypes] = useState([]);
    const [selectedType, setSelectedType] = useState('');
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteCodeId, setDeleteCodeId] = useState(null);

    useEffect(() => {
        fetchCodeTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedType) {
            fetchCodes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType]);

    const fetchCodeTypes = async () => {
        setLoading(true);
        try {
            const types = await commonCodeService.getCodeTypes();
            setCodeTypes(types);
            if (types.length > 0 && !selectedType) {
                setSelectedType(types[0]);
            }
        } catch (error) {
            console.error('Error fetching code types:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCodes = async () => {
        if (!selectedType) return;

        setLoading(true);
        try {
            const data = await commonCodeService.getCommonCodes(selectedType);
            setCodes(data.codes || []);
        } catch (error) {
            console.error('Error fetching codes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingCode(null);
        setShowModal(true);
    };

    const handleEdit = (code) => {
        setEditingCode(code);
        setShowModal(true);
    };

    const handleDelete = (codeId) => {
        setDeleteCodeId(codeId);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            await commonCodeService.deleteCommonCode(deleteCodeId);
            fetchCodes();
            setShowDeleteConfirm(false);
        } catch (error) {
            console.error('Error deleting code:', error);
        }
    };

    const handleSave = async (codeData) => {
        try {
            const submitData = { ...codeData, code_type: selectedType };
            if (editingCode) {
                await commonCodeService.updateCommonCode(editingCode.id, submitData);
            } else {
                await commonCodeService.createCommonCode(submitData);
            }
            setShowModal(false);
            fetchCodes();
        } catch (error) {
            console.error('Error saving code:', error);
            throw error;
        }
    };

    const formatCodeType = (type) => {
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
        <div className="space-y-6">
            <div className="bg-white flex justify-between items-center rounded-lg bxshadowhd mrgall py-3 padlr15 border">
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                    
                    Common Codes
                </h1>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 px-4 py-2 bg-scarlet-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Code
                </button>
            </div>
            <div className="px-4 py-0 flex gap-4 flex-end">
                    <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className="drop_bform px-4 py-2 bg-blue-700 text-white rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                        {codeTypes.map(type => (
                            <option key={type} value={type}>
                                {formatCodeType(type)}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={fetchCodes}
                        className="px-4 py-2 bg-scarlet-600 text-white border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            <div className="bg-white rounded-lg bxshadowhd p-2 mrgall border">
                

                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <CommonCodeTable
                        codes={codes}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                )}
            </div>

            <CommonCodeModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onSave={handleSave}
                code={editingCode}
                codeType={selectedType}
            />

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                title="Delete Code"
                message="Are you sure you want to delete this code? This action cannot be undone."
            />
        </div>
    );
};

export default CommonCodes;